import { Injectable } from '@angular/core';
import { ApiConversionHelper } from '../../helpers/api-conversion.helper';
import { MonitoringResource, ResourceConfiguration } from '../../types/monitoring/monitoring-resource.type';
import { ResourceSort } from '../../types/monitoring/resource-sort.type';
import { ResourcesStatusInfo } from '../../types/monitoring/resource-status-info.type';
import { ResourceFilter } from '../../types/monitoring/resources-filter.type';
import { BaseApiService } from './base-api.service';
import { DateHelper } from '../../helpers/date.helper';

@Injectable({ providedIn: 'root' })
export class ResourcesApiService extends BaseApiService {
  protected get baseUrl(): string {
    return `${super.baseUrl}/resources`;
  }

  async getResources(tenantId: string, pageSize: number, pageIndex: number, filter: ResourceFilter = null, sortBy: ResourceSort = null): Promise<MonitoringResource[]> {
    const result = await this.get<MonitoringResource[]>(tenantId ?? '', { pageSize, pageIndex, ...filter, ...sortBy });
    result.forEach(s => ApiConversionHelper.convertMonitoringResource(s));
    return result;
  }

  async getResourcesStatus(tenantId: string): Promise<ResourcesStatusInfo> {
    return await this.get<ResourcesStatusInfo>(`statuses${this.getTenantParameter(tenantId)}`);
  }

  async updateResourceConfiguration(resourceId: string, configuration: ResourceConfiguration): Promise<void> {
    ApiConversionHelper.convertResourceConfigurationForApi(configuration);
    return await this.patch(resourceId, configuration);
  }

  async setResourceIsEnabled(resourceId: string, isEnabled: boolean): Promise<void> {
    return await this.patch(`${resourceId}/is-enabled`, isEnabled);
  }

  async setResourceIsQuarantined(resourceId: string, isQuarantined: boolean, endOfQuarantine?: Date, reason?: string): Promise<void> {
    return await this.patch(`${resourceId}/is-quarantined`, { isQuarantined, endOfQuarantine: endOfQuarantine ? DateHelper.formatApiDateTime(endOfQuarantine) : undefined, reason });
  }

  async setResourceIsPinned(resourceId: string, isPinned: boolean): Promise<void> {
    return await this.patch(`${resourceId}/is-pinned`, { isPinned });
  }

  async assignResourceToGroups(resourceId: string, resourceGroupsIds: string[]): Promise<void> {
    return await this.patch(`${resourceId}/resource-groups`, resourceGroupsIds);
  }

  async toggleLicense(resourceId: string, enabled: boolean): Promise<void> {
    return await this.post(`${resourceId}/toggle-license`, enabled);
  }
}
